import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  fetchSchedules,
  bulkCreateSchedules,
  bulkUpdateSchedules,
  bulkDeleteSchedules,
  deactivateSchedule,
  cancelScheduleDeactivation,
  recreateSchedule,
} from "../../../../redux/admin/adminOperations";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Grid,
  Toolbar,
  AppBar,
  TextField,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  SettingsBackupRestore as RestoreIcon,
} from "@mui/icons-material";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { FormattedMessage, useIntl } from "react-intl";
import { Schedule, Class } from "../../../../redux/types/types";
import { useSelector } from "react-redux";
import AttendeeImportantInfoButton from "../../../Attendees/AttendeeImportantInfoButton";

interface LocalSchedule extends Schedule {
  isNew?: boolean;
  isEditing?: boolean;
  isDirty?: boolean;
  isDeleted?: boolean;
  startTimeErrorMessage?: string;
  endTimeErrorMessage?: string;
  startTimeFocused?: boolean;
  endTimeFocused?: boolean;
}

interface ScheduleManagerProps {
  classId: number;
}

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export default function ScheduleManager({ classId }: ScheduleManagerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [localSchedules, setLocalSchedules] = useState<LocalSchedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState(0);
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [deactivationModalOpen, setDeactivationModalOpen] = useState(false);
  const [selectedScheduleForDeactivation, setSelectedScheduleForDeactivation] =
    useState<LocalSchedule | null>(null);
  const [deactivationDate, setDeactivationDate] = useState("");
  const [deactivationDateError, setDeactivationDateError] = useState("");
  const [
    immediateDeactivationConfirmOpen,
    setImmediateDeactivationConfirmOpen,
  ] = useState(false);
  const [
    isImmediateDeactivationConfirmed,
    setIsImmediateDeactivationConfirmed,
  ] = useState(false);
  const [showDeactivatedSchedules, setShowDeactivatedSchedules] = useState(
    () => {
      const saved = localStorage.getItem("showDeactivatedSchedules");
      return saved ? JSON.parse(saved) : false;
    },
  );

  useEffect(() => {
    localStorage.setItem(
      "showDeactivatedSchedules",
      JSON.stringify(showDeactivatedSchedules),
    );
  }, [showDeactivatedSchedules]);

  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [selectedScheduleForRestore, setSelectedScheduleForRestore] =
    useState<LocalSchedule | null>(null);

  const selectedClass = useSelector((state: RootState) =>
    state.admin.classes.find((c: Class) => c.id === classId),
  );

  useEffect(() => {
    const newSchedules = localSchedules.filter((s) => s.isNew);
    if (newSchedules.length) {
      localStorage.setItem("newClassSchedules", JSON.stringify(newSchedules));
    }
  }, [localSchedules]);

  useEffect(() => {
    loadSchedules();
    //eslint-disable-next-line
  }, [dispatch, classId, intl, showDeactivatedSchedules]);

  const loadSchedules = async () => {
    try {
      const fetchedSchedules = await dispatch(
        fetchSchedules({
          classId,
          page: 1,
          limit: 100,
          sortBy: "dayOfWeek",
          sortOrder: "asc",
        }),
      ).unwrap();

      const savedSchedules = await localStorage.getItem("newClassSchedules");
      if (savedSchedules?.length) {
        await setLocalSchedules([
          ...fetchedSchedules.schedules
            .filter((schedule) => {
              if (showDeactivatedSchedules) {
                return true;
              }
              return (
                !schedule.deactivationDate ||
                new Date(schedule.deactivationDate) > new Date()
              );
            })
            .filter((schedule) => !schedule.recreated)
            .map((schedule) => ({
              ...schedule,
              id: Number(schedule.id),
              isDirty: false,
              isEditing: false,
              isDeleted: false,
              startTimeFocused: false,
              endTimeFocused: false,
            })),
          ...JSON.parse(savedSchedules),
        ]);
      } else {
        await setLocalSchedules(
          fetchedSchedules.schedules
            .filter((schedule) => {
              if (showDeactivatedSchedules) {
                return true;
              }
              return (
                !schedule.deactivationDate ||
                new Date(schedule.deactivationDate) > new Date()
              );
            })
            .filter((schedule) => !schedule.recreated)
            .map((schedule) => ({
              ...schedule,
              id: Number(schedule.id),
              isDirty: false,
              isEditing: false,
              isDeleted: false,
              startTimeFocused: false,
              endTimeFocused: false,
            })),
        );
      }
      Notify.success(
        intl.formatMessage({ id: "admin.dashboard.schedulesLoadedSuccess" }),
      );
    } catch (error) {
      console.error("Failed to fetch schedules:", error);
      Notify.failure(
        intl.formatMessage({ id: "admin.dashboard.schedulesLoadedError" }),
      );
    }
  };

  const handleAddSchedule = (dayOfWeek: number, repeatWeekly: boolean) => {
    const newSchedule: LocalSchedule = {
      id: Date.now(),
      dayOfWeek,
      startTime: "",
      endTime: "",
      repeatWeekly,
      classId,
      isNew: true,
      isEditing: true,
      isDirty: true,
      isDeleted: false,
      date: repeatWeekly ? null : new Date(),
      startTimeErrorMessage: "",
      endTimeErrorMessage: "",
      startTimeFocused: false,
      endTimeFocused: false,
      recreated: false,
    };
    setLocalSchedules([...localSchedules, newSchedule]);
  };

  const handleUpdateSchedule = (updatedSchedule: LocalSchedule) => {
    const updatedSchedules = localSchedules.map((s) =>
      s.id === updatedSchedule.id ? { ...updatedSchedule, isDirty: true } : s,
    );
    setLocalSchedules(updatedSchedules);
  };

  const handleDeleteSchedule = (scheduleId: number) => {
    const updatedSchedules = localSchedules.map((s) =>
      s.id === scheduleId ? { ...s, isDeleted: true, isDirty: true } : s,
    );
    setLocalSchedules(updatedSchedules);
    if (selectedScheduleId === scheduleId) {
      setSelectedScheduleId(null);
    }
  };

  const handleFocusChange = (
    schedule: LocalSchedule,
    field: "startTimeFocused" | "endTimeFocused",
    isFocused: boolean,
  ) => {
    const updatedSchedule = {
      ...schedule,
      [field]: isFocused,
    };
    setLocalSchedules((prevSchedules) =>
      prevSchedules.map((s) =>
        s.id === updatedSchedule.id ? updatedSchedule : s,
      ),
    );
  };

  const hasErrors = localSchedules.some(
    (schedule) =>
      (!!schedule.startTimeErrorMessage && !schedule.startTimeFocused) ||
      (!!schedule.endTimeErrorMessage && !schedule.endTimeFocused),
  );

  const handleSaveAllChanges = async () => {
    const schedulesToSave = localSchedules.filter((s) => !s.isDeleted);
    const schedulesToCreate = schedulesToSave.filter((s) => s.isNew);
    const schedulesToUpdate = schedulesToSave.filter(
      (s) => s.isDirty && !s.isNew,
    );
    const schedulesToDelete = localSchedules.filter(
      (s) => s.isDeleted && !s.isNew,
    );

    try {
      if (schedulesToDelete.length > 0) {
        await dispatch(
          bulkDeleteSchedules(schedulesToDelete.map((s) => s.id)),
        ).unwrap();
      }
      if (schedulesToUpdate.length > 0) {
        await dispatch(bulkUpdateSchedules(schedulesToUpdate)).unwrap();
      }
      if (schedulesToCreate.length > 0) {
        await dispatch(bulkCreateSchedules(schedulesToCreate)).unwrap();
      }

      Notify.success(
        intl.formatMessage({ id: "admin.dashboard.schedulesSavedSuccess" }),
      );
      await localStorage.removeItem("newClassSchedules");
      await loadSchedules();
    } catch (error) {
      console.error("Failed to save schedules:", error);
      Notify.failure(
        intl.formatMessage({ id: "admin.dashboard.schedulesSavedError" }),
      );
    }
  };

  const handleTimeChange = (
    schedule: LocalSchedule,
    field: "startTime" | "endTime",
    value: string,
  ) => {
    const updatedSchedule = {
      ...schedule,
      [field]: value,
      isEditing: false,
    };

    const timeFormat = /^([01]\d|2[0-3]):([0-5]\d)$/;
    const isValidFormat = timeFormat.test(value);
    let errorMessage = "";

    if (!isValidFormat) {
      errorMessage = intl.formatMessage({
        id: "admin.dashboard.invalidTimeFormatError",
      });
    }

    if (field === "startTime") {
      updatedSchedule.startTimeErrorMessage = errorMessage;
    } else {
      updatedSchedule.endTimeErrorMessage = errorMessage;
    }

    const startTimeStr = updatedSchedule.startTime;
    const endTimeStr = updatedSchedule.endTime;

    const startTimeValid = timeFormat.test(startTimeStr);
    const endTimeValid = timeFormat.test(endTimeStr);

    if (startTimeValid && endTimeValid) {
      const startMinutes = timeToMinutes(startTimeStr);
      const endMinutes = timeToMinutes(endTimeStr);

      if (startMinutes >= endMinutes) {
        const invalidTimeErrorMessage = intl.formatMessage({
          id: "admin.dashboard.invalidTimeError",
        });
        if (field === "startTime") {
          updatedSchedule.startTimeErrorMessage = invalidTimeErrorMessage;
        } else {
          updatedSchedule.endTimeErrorMessage = invalidTimeErrorMessage;
        }
      } else {
        updatedSchedule.startTimeErrorMessage = "";
        updatedSchedule.endTimeErrorMessage = "";
      }
    }

    handleUpdateSchedule(updatedSchedule);
  };

  function timeToMinutes(timeStr: string): number {
    const [hoursStr, minutesStr] = timeStr.split(":");
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    return hours * 60 + minutes;
  }

  const handleDateChange = (schedule: LocalSchedule, value: string) => {
    const selectedDate = value.length ? new Date(value) : new Date();
    const dayOfWeek = selectedDate.getDay();
    const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const updatedSchedule = {
      ...schedule,
      date: selectedDate,
      dayOfWeek: adjustedDayOfWeek,
      isEditing: false,
    };
    handleUpdateSchedule(updatedSchedule);
  };

  const handleAccordionChange = async (schedule: any) => {
    if (selectedScheduleId !== schedule.id && !schedule.isNew) {
      setSelectedScheduleId(schedule.id);
    } else {
      setSelectedScheduleId(null);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenDeactivationModal = (schedule: LocalSchedule) => {
    setSelectedScheduleForDeactivation(schedule);
    setDeactivationDate("");
    setDeactivationModalOpen(true);
  };

  const handleCloseDeactivationModal = () => {
    setDeactivationModalOpen(false);
    setSelectedScheduleForDeactivation(null);
    setDeactivationDate("");
    setDeactivationDateError("");
    setIsImmediateDeactivationConfirmed(false);
  };

  const validateDeactivationDate = (date: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (!date) {
      setDeactivationDateError(
        intl.formatMessage({ id: "admin.dashboard.deactivationDateRequired" }),
      );
      return false;
    }

    if (selectedDate < today) {
      setDeactivationDateError(
        intl.formatMessage({
          id: "admin.dashboard.deactivationDateMustBeFuture",
        }),
      );
      return false;
    }

    setDeactivationDateError("");
    return true;
  };

  const handleDeactivationDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newDate = e.target.value;

    // Simple validation for iOS text input format (YYYY-MM-DD)
    if (isIOS() && newDate.length === 10) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(newDate)) {
        setDeactivationDateError(
          intl.formatMessage({ id: "admin.dashboard.invalidDateFormat" }),
        );
        return;
      }

      const [year, month, day] = newDate.split("-").map(Number);
      const isValidDate = !isNaN(new Date(year, month - 1, day).getTime());
      if (!isValidDate) {
        setDeactivationDateError(
          intl.formatMessage({ id: "admin.dashboard.invalidDate" }),
        );
        return;
      }
    }

    setDeactivationDate(newDate);
    setIsImmediateDeactivationConfirmed(false);

    const selectedDate = new Date(newDate);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if the schedule is for today and has already started
    if (selectedScheduleForDeactivation) {
      const currentDayOfWeek = today.getDay();
      const adjustedCurrentDayOfWeek =
        currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

      if (
        selectedScheduleForDeactivation.dayOfWeek === adjustedCurrentDayOfWeek
      ) {
        const currentTime = new Date();
        const [hours, minutes] = selectedScheduleForDeactivation.startTime
          .split(":")
          .map(Number);
        const scheduleStartTime = new Date();
        scheduleStartTime.setHours(hours, minutes, 0, 0);

        if (currentTime > scheduleStartTime) {
          // If schedule has already started today, set min date to tomorrow
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          if (selectedDate < tomorrow) {
            setDeactivationDateError(
              intl.formatMessage({
                id: "admin.dashboard.deactivationDateMustBeTomorrow",
              }),
            );
            return;
          }
        }
      }
    }

    if (
      selectedDate.getTime() === today.getTime() &&
      !isImmediateDeactivationConfirmed
    ) {
      setImmediateDeactivationConfirmOpen(true);
      setDeactivationDateError("");
    } else {
      validateDeactivationDate(newDate);
    }
  };

  const handleCloseImmediateDeactivation = () => {
    setImmediateDeactivationConfirmOpen(false);
    setDeactivationDate("");
    setIsImmediateDeactivationConfirmed(false);
  };

  const handleConfirmImmediateDeactivation = () => {
    setImmediateDeactivationConfirmOpen(false);
    setIsImmediateDeactivationConfirmed(true);
  };

  const handleDeactivateSchedule = async () => {
    if (
      selectedScheduleForDeactivation &&
      (validateDeactivationDate(deactivationDate) ||
        isImmediateDeactivationConfirmed)
    ) {
      try {
        await dispatch(
          deactivateSchedule({
            scheduleId: selectedScheduleForDeactivation.id,
            deactivationDate,
          }),
        ).unwrap();
        Notify.success(
          intl.formatMessage({
            id: "admin.dashboard.scheduleDeactivatedSuccess",
          }),
        );
        handleCloseDeactivationModal();
        setIsImmediateDeactivationConfirmed(false);
        await loadSchedules();
      } catch (error) {
        console.error("Failed to deactivate schedule:", error);
        Notify.failure(
          intl.formatMessage({
            id: "admin.dashboard.scheduleDeactivatedError",
          }),
        );
      }
    }
  };

  const handleCancelDeactivation = async (schedule: LocalSchedule) => {
    try {
      await dispatch(cancelScheduleDeactivation(schedule.id)).unwrap();
      Notify.success(
        intl.formatMessage({
          id: "admin.dashboard.scheduleDeactivationCancelledSuccess",
        }),
      );
      await loadSchedules();
    } catch (error) {
      console.error("Failed to cancel schedule deactivation:", error);
      Notify.failure(
        intl.formatMessage({
          id: "admin.dashboard.scheduleDeactivationCancelledError",
        }),
      );
    }
  };

  const handleRestore = (schedule: LocalSchedule) => {
    setSelectedScheduleForRestore(schedule);
    setRestoreConfirmOpen(true);
  };

  const handleCloseRestoreConfirm = () => {
    setRestoreConfirmOpen(false);
    setSelectedScheduleForRestore(null);
  };

  const handleConfirmRestore = async () => {
    if (selectedScheduleForRestore) {
      try {
        await dispatch(
          recreateSchedule(selectedScheduleForRestore.id),
        ).unwrap();
        console.log(selectedScheduleForRestore);
        Notify.success(
          intl.formatMessage({
            id: "admin.dashboard.scheduleRestoredSuccess",
          }),
        );
        setRestoreConfirmOpen(false);
        setSelectedScheduleForRestore(null);
        await loadSchedules();
      } catch (error) {
        console.error("Failed to restore schedule:", error);
        Notify.failure(
          intl.formatMessage({ id: "admin.dashboard.scheduleRestoredError" }),
        );
      }
    }
  };

  const renderSchedules = (repeatWeekly: boolean) => {
    return (
      <Grid container spacing={2}>
        {repeatWeekly && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Checkbox
              checked={showDeactivatedSchedules}
              onChange={() => setShowDeactivatedSchedules((prev: any) => !prev)}
              color="primary"
              disabled={Boolean(
                selectedClass?.cancelDate &&
                  new Date(selectedClass.cancelDate) < new Date(),
              )}
            />
            <Typography variant="body1">
              <FormattedMessage id="admin.dashboard.showDeactivatedSchedules" />
            </Typography>
          </Box>
        )}
        {repeatWeekly ? (
          daysOfWeek.map((day, index) => (
            <Grid item xs={12} key={day}>
              <Paper sx={{ p: 2 }}>
                <Box
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  justifyContent="space-between"
                  alignItems={isMobile ? "flex-start" : "center"}
                  mb={2}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: isMobile ? 1 : 0,
                      wordBreak: "break-word",
                      maxWidth: "100%",
                    }}
                  >
                    <FormattedMessage id={`admin.dashboard.${day}`} />
                  </Typography>
                  <IconButton
                    onClick={() => handleAddSchedule(index, true)}
                    color="primary"
                    sx={{ alignSelf: isMobile ? "flex-end" : "center" }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                {renderScheduleList(index, true)}
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
                mb={2}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: isMobile ? 1 : 0,
                    wordBreak: "break-word",
                    maxWidth: "100%",
                  }}
                >
                  <FormattedMessage id="admin.dashboard.oneTimeSchedules" />
                </Typography>
                <IconButton
                  onClick={() => handleAddSchedule(0, false)}
                  color="primary"
                  sx={{ alignSelf: isMobile ? "flex-end" : "center" }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              {renderScheduleList(null, false)}
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderScheduleList = (
    dayOfWeek: number | null,
    repeatWeekly: boolean,
  ) => {
    const filteredSchedules = localSchedules.filter(
      (s) =>
        !s.isDeleted &&
        s.repeatWeekly === repeatWeekly &&
        (dayOfWeek === null || s.dayOfWeek === dayOfWeek),
    );

    return filteredSchedules.length > 0 ? (
      filteredSchedules.map((schedule) => (
        <Box key={schedule.id} sx={{ mb: 2 }}>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ width: "100%" }}
          >
            {!repeatWeekly && (
              <TextField
                label={intl.formatMessage({ id: "admin.dashboard.date" })}
                type="date"
                value={
                  schedule.date
                    ? new Date(schedule.date).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => handleDateChange(schedule, e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  mr: isMobile ? 0 : 2,
                  mb: isMobile ? 1 : 0,
                  width: isMobile ? "100%" : "auto",
                }}
              />
            )}
            <TextField
              label={intl.formatMessage({ id: "admin.dashboard.startTime" })}
              type="text"
              placeholder="HH:mm"
              value={schedule.startTime}
              onChange={(e) =>
                handleTimeChange(schedule, "startTime", e.target.value)
              }
              onFocus={() =>
                handleFocusChange(schedule, "startTimeFocused", true)
              }
              onBlur={() =>
                handleFocusChange(schedule, "startTimeFocused", false)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 5 }}
              error={
                !schedule.startTimeFocused && !!schedule.startTimeErrorMessage
              }
              helperText={
                !schedule.startTimeFocused ? schedule.startTimeErrorMessage : ""
              }
              sx={{
                mr: isMobile ? 0 : 2,
                mb: isMobile ? 1 : 0,
                width: isMobile ? "100%" : "auto",
              }}
            />
            <TextField
              label={intl.formatMessage({ id: "admin.dashboard.endTime" })}
              type="text"
              placeholder="HH:mm"
              value={schedule.endTime}
              onChange={(e) =>
                handleTimeChange(schedule, "endTime", e.target.value)
              }
              onFocus={() =>
                handleFocusChange(schedule, "endTimeFocused", true)
              }
              onBlur={() =>
                handleFocusChange(schedule, "endTimeFocused", false)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 5 }}
              error={!schedule.endTimeFocused && !!schedule.endTimeErrorMessage}
              helperText={
                !schedule.endTimeFocused ? schedule.endTimeErrorMessage : ""
              }
              sx={{
                mr: isMobile ? 0 : 2,
                mb: isMobile ? 1 : 0,
                width: isMobile ? "100%" : "auto",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "row" : "row",
                justifyContent: isMobile ? "space-between" : "flex-start",
                alignItems: "center",
                width: isMobile ? "100%" : "auto",
                mt: isMobile ? 1 : 0,
              }}
            >
              {schedule.deactivationDate && (
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    mr: 2,
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    color="warning.main"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      wordBreak: "break-word",
                    }}
                  >
                    {new Date(schedule.deactivationDate).toLocaleDateString()}
                  </Typography>
                  {!schedule.deactivationDate ||
                    (new Date(schedule.deactivationDate) > new Date() ? (
                      <IconButton
                        onClick={() => handleCancelDeactivation(schedule)}
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <BlockIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => handleRestore(schedule)}
                        color="success"
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <RestoreIcon />
                      </IconButton>
                    ))}
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={() => handleDeleteSchedule(schedule.id)}
                  color="error"
                  disabled={
                    schedule.attendees &&
                    schedule.attendees.filter((a) => !a.unenrollDate).length > 0
                  }
                >
                  <DeleteIcon />
                </IconButton>
                {!schedule.isNew && !schedule.date && (
                  <IconButton
                    onClick={() => handleOpenDeactivationModal(schedule)}
                    color="warning"
                    disabled={
                      !!schedule.deactivationDate ||
                      (selectedClass?.cancelDate &&
                        new Date(selectedClass.cancelDate) < new Date())
                    }
                  >
                    <BlockIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
          {!schedule.isNew && (
            <Accordion
              expanded={selectedScheduleId === schedule.id}
              onChange={() => handleAccordionChange(schedule)}
              sx={{ mt: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  <FormattedMessage id="admin.dashboard.attendees" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {schedule.attendees &&
                    schedule.attendees.map((attendee, index) => {
                      return (
                        !attendee.unenrollDate && (
                          <ListItem
                            key={attendee.id}
                            sx={{
                              flexDirection: "column",
                              alignItems: "stretch",
                              gap: 0.5,
                            }}
                          >
                            <ListItemText
                              primary={`${index + 1}. ${attendee.firstName} ${
                                attendee.lastName
                              }`}
                            />
                            <AttendeeImportantInfoButton attendee={attendee} />
                          </ListItem>
                        )
                      );
                    })}
                </List>
                {schedule.attendees && schedule.attendees.length === 0 && (
                  <Typography color="textSecondary">
                    <FormattedMessage id="admin.dashboard.noAttendees" />
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      ))
    ) : (
      <Typography color="textSecondary">
        <FormattedMessage id="admin.dashboard.unavailable" />
      </Typography>
    );
  };

  // Add this function to detect iOS platform
  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    );
  };

  return (
    <Box sx={{ flexGrow: 1, position: "relative", height: "100%" }}>
      <AppBar position="static" color="default">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <FormattedMessage id="admin.dashboard.scheduleManager" />
          </Typography>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            "& .MuiTab-root": {
              minWidth: "auto",
              padding: isMobile ? "6px 8px" : "12px 16px",
              fontSize: isMobile ? "0.8rem" : "inherit",
              whiteSpace: "normal",
              lineHeight: isMobile ? 1.2 : "inherit",
            },
          }}
        >
          <Tab
            label={<FormattedMessage id="admin.dashboard.weeklySchedules" />}
            sx={{ wordBreak: "break-word", textAlign: "center" }}
          />
          <Tab
            label={<FormattedMessage id="admin.dashboard.oneTimeSchedules" />}
            sx={{ wordBreak: "break-word", textAlign: "center" }}
          />
        </Tabs>
      </AppBar>
      <Box sx={{ height: "calc(100% - 112px)", overflowY: "auto", pb: 8 }}>
        <Paper sx={{ p: 2, m: 2 }}>
          {activeTab === 0 ? renderSchedules(true) : renderSchedules(false)}
        </Paper>
      </Box>
      <Box
        sx={{
          position: "sticky",
          bottom: -20,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: "white",
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          zIndex: 9999,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAllChanges}
          disabled={hasErrors}
          sx={{
            minWidth: isMobile ? "100%" : "auto",
          }}
        >
          <FormattedMessage id="admin.dashboard.saveAllChanges" />
        </Button>
      </Box>
      <Dialog
        open={deactivationModalOpen}
        onClose={handleCloseDeactivationModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage id="admin.dashboard.deactivateSchedule" />
        </DialogTitle>
        <DialogContent>
          {isIOS() ? (
            <TextField
              label={intl.formatMessage({
                id: "admin.dashboard.deactivationDate",
              })}
              type="text"
              placeholder="YYYY-MM-DD"
              value={deactivationDate}
              onChange={(e) => {
                const value = e.target.value;
                setDeactivationDate(value);
                if (value && value.length >= 10) {
                  handleDeactivationDateChange({
                    target: { value },
                  } as React.ChangeEvent<HTMLInputElement>);
                }
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                pattern: "\\d{4}-\\d{2}-\\d{2}",
                maxLength: 10,
              }}
              error={!!deactivationDateError}
              helperText={
                deactivationDateError ||
                intl.formatMessage({ id: "admin.dashboard.dateFormatHint" })
              }
              fullWidth
              sx={{ mt: 2 }}
            />
          ) : (
            <TextField
              label={intl.formatMessage({
                id: "admin.dashboard.deactivationDate",
              })}
              type="date"
              value={deactivationDate}
              onChange={handleDeactivationDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: (() => {
                  if (selectedScheduleForDeactivation) {
                    const today = new Date();
                    const currentDayOfWeek = today.getDay();
                    const adjustedCurrentDayOfWeek =
                      currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;

                    if (
                      selectedScheduleForDeactivation.dayOfWeek ===
                      adjustedCurrentDayOfWeek
                    ) {
                      const currentTime = new Date();
                      const [hours, minutes] =
                        selectedScheduleForDeactivation.startTime
                          .split(":")
                          .map(Number);
                      const scheduleStartTime = new Date();
                      scheduleStartTime.setHours(hours, minutes, 0, 0);

                      if (currentTime > scheduleStartTime) {
                        const tomorrow = new Date(today);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        return tomorrow.toISOString().split("T")[0];
                      }
                    }
                  }
                  return new Date().toISOString().split("T")[0];
                })(),
              }}
              error={!!deactivationDateError}
              helperText={deactivationDateError}
              fullWidth
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivationModal}>
            <FormattedMessage id="admin.dashboard.cancel" />
          </Button>
          <Button
            onClick={handleDeactivateSchedule}
            variant="contained"
            color="warning"
            disabled={
              !deactivationDate ||
              (!!deactivationDateError && !isImmediateDeactivationConfirmed)
            }
          >
            <FormattedMessage id="admin.dashboard.deactivate" />
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={immediateDeactivationConfirmOpen}
        onClose={handleCloseImmediateDeactivation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage id="admin.dashboard.immediateDeactivationTitle" />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage id="admin.dashboard.immediateDeactivationMessage" />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImmediateDeactivation}>
            <FormattedMessage id="admin.dashboard.cancel" />
          </Button>
          <Button
            onClick={handleConfirmImmediateDeactivation}
            variant="contained"
            color="warning"
          >
            <FormattedMessage id="admin.dashboard.proceed" />
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={restoreConfirmOpen}
        onClose={handleCloseRestoreConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage
            id="admin.dashboard.restoreScheduleTitle"
            defaultMessage="Restore Schedule"
          />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage
              id="admin.dashboard.restoreScheduleMessage"
              defaultMessage="Are you sure you want to restore this schedule?"
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRestoreConfirm}>
            <FormattedMessage id="admin.dashboard.cancel" />
          </Button>
          <Button
            onClick={handleConfirmRestore}
            variant="contained"
            color="success"
          >
            <FormattedMessage
              id="admin.dashboard.restore"
              defaultMessage="Restore"
            />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
